import React, { useState, useEffect } from "react";

const navStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Jost:wght@300;400;500&display=swap');

  :root {
    --warm-white: #fdfaf6;
    --gold: #c9a96e;
    --gold-light: #e8d5b7;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.4rem clamp(1rem, 4vw, 3rem);
    background: transparent;
    transition: background 0.4s ease, padding 0.4s ease, box-shadow 0.4s ease;
    overflow: hidden;
  }

  nav.scrolled {
    background: rgba(18, 14, 10, 0.92);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    padding: 1rem clamp(1rem, 4vw, 3rem);
    box-shadow: 0 1px 0 rgba(201, 169, 110, 0.2);
  }

  .nav-logo {
    font-family: 'Cormorant Garamond', serif;
    font-weight: 700;
    font-size: clamp(1.1rem, 3vw, 1.55rem);
    color: var(--warm-white);
    text-decoration: none;
    letter-spacing: 0.04em;
    line-height: 1.1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    transition: opacity 0.3s ease;
    z-index: 1001;
    flex-shrink: 0;
    min-width: 0;
  }

  .nav-logo:hover {
    opacity: 0.85;
  }

  .nav-logo .brand-name {
    display: block;
    font-size: clamp(1.1rem, 3vw, 1.55rem);
    font-weight: 700;
    color: var(--warm-white);
    white-space: nowrap;
  }

  .nav-logo .brand-sub {
    display: block;
    font-family: 'Jost', sans-serif;
    font-weight: 300;
    font-size: clamp(0.45rem, 1.2vw, 0.62rem);
    letter-spacing: 0.28em;
    color: var(--gold);
    text-transform: uppercase;
    margin-top: 1px;
    white-space: nowrap;
  }

  /* Desktop Navigation */
  .nav-links {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: clamp(1rem, 2.5vw, 2.2rem);
    align-items: center;
    flex-wrap: nowrap;
    flex-shrink: 1;
    min-width: 0;
  }

  .nav-links li {
    flex-shrink: 0;
  }

  .nav-links li a {
    font-family: 'Jost', sans-serif;
    font-weight: 400;
    font-size: clamp(0.65rem, 1.1vw, 0.82rem);
    letter-spacing: clamp(0.08em, 0.5vw, 0.18em);
    text-transform: uppercase;
    color: var(--warm-white);
    text-decoration: none;
    position: relative;
    padding-bottom: 3px;
    transition: color 0.3s ease;
    white-space: nowrap;
  }

  .nav-links li a::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 1px;
    background: var(--gold);
    transition: width 0.3s ease;
  }

  .nav-links li a:hover {
    color: var(--gold-light);
  }

  .nav-links li a:hover::after {
    width: 100%;
  }

  .nav-cta {
    font-family: 'Jost', sans-serif;
    font-weight: 500;
    font-size: clamp(0.65rem, 1vw, 0.78rem);
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #1a1208;
    background: var(--gold);
    border: none;
    padding: 0.65rem clamp(0.8rem, 2vw, 1.5rem);
    cursor: pointer;
    transition: background 0.3s ease, transform 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .nav-cta:hover {
    background: var(--gold-light);
    transform: translateY(-1px);
  }

  .nav-cta:active {
    transform: translateY(0);
  }

  /* Hamburger Menu Button */
  .hamburger {
    display: none;
    flex-direction: column;
    justify-content: space-between;
    width: 28px;
    height: 18px;
    background: transparent;
    border: none;
    cursor: pointer;
    z-index: 1001;
    flex-shrink: 0;
    padding: 0;
  }

  .hamburger span {
    display: block;
    width: 100%;
    height: 2px;
    background: #ffffff;
    border-radius: 2px;
    transition: all 0.3s ease;
  }

  .hamburger.open span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }

  .hamburger.open span:nth-child(2) {
    opacity: 0;
  }

  .hamburger.open span:nth-child(3) {
    transform: rotate(-45deg) translate(5px, -5px);
  }

  /* Mobile Menu */
  .mobile-menu {
    position: fixed;
    top: 0;
    right: -100%;
    width: min(80%, 400px);
    height: 100vh;
    height: 100dvh;
    background: rgba(18, 14, 10, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    z-index: 1000;
    transition: right 0.4s ease;
    padding: 5rem 2rem 2rem;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  .mobile-menu.open {
    right: 0;
  }

  .mobile-nav-links {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .mobile-nav-links li a {
    font-family: 'Jost', sans-serif;
    font-weight: 400;
    font-size: clamp(1rem, 4vw, 1.2rem);
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--warm-white);
    text-decoration: none;
    display: block;
    padding: 0.5rem 0;
    transition: color 0.3s ease, transform 0.3s ease;
  }

  .mobile-nav-links li a:hover {
    color: var(--gold-light);
    transform: translateX(10px);
  }

  .mobile-cta {
    margin-top: 2rem;
    font-family: 'Jost', sans-serif;
    font-weight: 500;
    font-size: 0.9rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #1a1208;
    background: var(--gold);
    border: none;
    padding: 0.85rem 1.5rem;
    cursor: pointer;
    transition: background 0.3s ease;
    width: 100%;
  }

  .mobile-cta:hover {
    background: var(--gold-light);
  }

  /* Overlay */
  .menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }

  .menu-overlay.open {
    opacity: 1;
    visibility: visible;
  }

  /* Hide hamburger on desktop */
  @media (min-width: 769px) {
    .hamburger {
      display: none !important;
    }
    .mobile-menu,
    .menu-overlay {
      display: none !important;
    }
  }

  /* Tablet: hide CTA, keep links */
  @media (min-width: 769px) and (max-width: 1024px) {
    .nav-links {
      gap: clamp(0.6rem, 1.8vw, 1.4rem);
    }
    .nav-links li a {
      font-size: clamp(0.6rem, 1vw, 0.72rem);
      letter-spacing: 0.1em;
    }
    .nav-cta {
      padding: 0.5rem 0.9rem;
      font-size: 0.68rem;
      letter-spacing: 0.12em;
    }
  }

  /* Mobile: switch to hamburger */
  @media (max-width: 768px) {
    nav {
      padding: 1rem 1.25rem;
    }
    nav.scrolled {
      padding: 0.75rem 1.25rem;
    }
    .nav-links,
    .nav-cta {
      display: none !important;
    }
    .hamburger {
      display: flex !important;
    }
  }

  /* Very small screens */
  @media (max-width: 360px) {
    nav {
      padding: 0.85rem 1rem;
    }
    .nav-logo .brand-name {
      font-size: 1rem;
    }
    .hamburger {
      width: 24px;
      height: 16px;
    }
  }
`;

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navItems = [
    "Home",
    "About",
    "Services",
    "Gallery",
    "Pricing",
    "Contact",
  ];

  return (
    <>
      <style>{navStyles}</style>
      <nav className={scrolled ? "scrolled" : ""}>
        <a
          href="#"
          className="nav-logo"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("home");
          }}
        >
          <span className="brand-name">
            Mani <span style={{ color: "var(--gold)" }}>Spa & Retreat</span>
          </span>
          <span className="brand-sub">Luxury Wellness</span>
        </a>

        {/* Desktop Navigation */}
        <ul className="nav-links">
          {navItems.map((l) => (
            <li key={l}>
              <a
                href={`#${l.toLowerCase()}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(l.toLowerCase());
                }}
              >
                {l}
              </a>
            </li>
          ))}
        </ul>

        <button className="nav-cta" onClick={() => scrollToSection("contact")}>
          Book Now
        </button>

        {/* Hamburger */}
        <button
          className={`hamburger ${mobileMenuOpen ? "open" : ""}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>

      {/* Mobile Overlay */}
      <div
        className={`menu-overlay ${mobileMenuOpen ? "open" : ""}`}
        onClick={closeMobileMenu}
      />

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        <ul className="mobile-nav-links">
          {navItems.map((l) => (
            <li key={l}>
              <a
                href={`#${l.toLowerCase()}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(l.toLowerCase());
                }}
              >
                {l}
              </a>
            </li>
          ))}
        </ul>
        <button
          className="mobile-cta"
          onClick={() => scrollToSection("contact")}
        >
          Book Now
        </button>
      </div>
    </>
  );
}

export default Nav;
